import { z } from 'zod';
import { SEAT_DETAIL_MAX, saleDispositionSchema, seatAvailabilitySchema, seatingAvailabilitySummarySchema, tableSaleModeSchema, } from "./seating-configuration-schemas.js";
import { moneySchema } from "./event-schemas.js";
import { accessibilityKindSchema, canvasSchema, geometrySchema, geometrySchemaVersionSchema, sectionKindSchema, topologyDigestSchema, } from "./venue-map-schemas.js";
/**
 * Buying a reserved seat.
 *
 * The attendee half of what 10B built for organizers, and deliberately a
 * separate surface rather than the same routes with a different reader. An
 * organizer configures a room; a customer chooses a chair in it. They need
 * different facts, and the ones an organizer needs — allocations, frozen
 * version identifiers, dispositions of chairs nobody may buy — are either
 * meaningless or actively unhelpful to a person picking a seat.
 *
 * What a client may never do here, whatever it renders:
 *
 *   - treat a selection as a reservation. Clicking a chair reserves nothing;
 *     only a server hold does.
 *   - nominate the commercial terms. A client says which chairs, or which
 *     table; the server resolves product, frozen version and allocation from
 *     the active configuration.
 *   - decide a hold has expired. A countdown is presentation. Whether a hold
 *     may still become a purchase is the server's answer at checkout.
 */
// --- The room, as a customer sees it ----------------------------------------
/**
 * The selling configuration, without a per-seat array.
 *
 * Only ever the ACTIVE version — a draft is an organizer's work in progress and
 * a customer has no business seeing a room that may never go on sale. The
 * aggregates are the same server-owned ones the Organizer view uses, for the
 * same reason: a browser under level-of-detail does not hold every chair, so a
 * count it derived would be wrong exactly when the room is largest.
 */
export const customerSeatingViewSchema = z.object({
    eventSessionId: z.string().uuid(),
    seatingConfigurationVersionId: z.string().uuid(),
    venueMapVersionId: z.string().uuid(),
    /**
     * The version token a later request carries back.
     *
     * If the organizer supersedes the configuration while somebody is choosing,
     * their next request names a version that is no longer selling and is
     * refused rather than quietly answered against a different room.
     */
    configurationDigest: z.string().regex(/^[0-9a-f]{64}$/),
    summary: seatingAvailabilitySummarySchema,
});
// --- The room, drawn --------------------------------------------------------
/**
 * The physical room, as a customer's renderer needs it.
 *
 * Deliberately its own schema rather than the Organizer topology re-exported.
 * They describe the same chairs and they are not the same contract: the
 * Organizer view is authoring state, and every field added to it in future
 * would otherwise become customer-visible the moment somebody added it. This
 * type is the list of things we have decided a stranger may see, and adding to
 * it has to be a deliberate act.
 *
 * What is *not* here is as much the point as what is. No claim state, no
 * availability, no allocation, no frozen product version, no price. Topology is
 * immutable for a given map version; every one of those changes underneath it,
 * and mixing the two would make an unchanging document uncacheable and give a
 * client two sources for the same answer. Availability comes from the lean
 * seating view and from bounded `seat-options`.
 *
 * The caller never nominates a map version. The server resolves
 * session -> ACTIVE configuration -> pinned map version, so this cannot be used
 * to browse an organizer's unpublished rooms.
 */
export const customerTopologyRowSchema = z.object({
    rowId: z.string().uuid(),
    /** What a customer is told: "Row 12", not the authoring code. */
    displayName: z.string(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const customerTopologyTableSchema = z.object({
    tableId: z.string().uuid(),
    displayName: z.string(),
    /** Chairs the furniture holds. Furniture, not inventory. */
    seatCapacity: z.number().int(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const customerTopologySectionSchema = z.object({
    sectionId: z.string().uuid(),
    displayName: z.string(),
    kind: sectionKindSchema,
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
    rows: z.array(customerTopologyRowSchema),
    tables: z.array(customerTopologyTableSchema),
});
/**
 * One chair, placed.
 *
 * `venueMapSeatId` is here because it is the whole reason this surface exists:
 * without it a browser has no authorized way to learn a single seat identifier,
 * and `seat-options` — which is keyed on exactly those identifiers — cannot be
 * called at all.
 *
 * The durable `seatIdentityId` is deliberately absent. It is the key a re-seat
 * migration follows across map versions, it is meaningless to a customer, and
 * a client holding it might use it to reinterpret a chair under a different
 * version, which is precisely what the occurrence exists to prevent.
 *
 * `isAccessible` and `hasObstructedView` are physical facts and are shown,
 * because they change whether a seat suits the person buying it. Neither
 * implies a price.
 */
export const customerTopologySeatSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    seatLabel: z.string(),
    sectionId: z.string().uuid(),
    rowId: z.string().uuid().nullable(),
    tableId: z.string().uuid().nullable(),
    isAccessible: z.boolean(),
    /**
     * What kind of accessible provision this chair is, when it is one.
     *
     * The same authoritative vocabulary the organizer authored, not a coarsened
     * customer version of it: "wheelchair space" and "transfer seat" are
     * different places and somebody choosing between them is answering a
     * different question each time. Flattening them would make the customer
     * surface less useful than the data behind it.
     *
     * Null for an ordinary chair, and structurally in step with `isAccessible` —
     * the two are one fact rendered twice, never two facts that can disagree.
     * `isAccessible` stays for compatibility with clients that only need the
     * coarse answer.
     */
    accessibilityProvision: accessibilityKindSchema.nullable(),
    hasObstructedView: z.boolean(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const customerTopologySchema = z.object({
    eventSessionId: z.string().uuid(),
    /** The room these chairs belong to. A client must not mix versions. */
    venueMapVersionId: z.string().uuid(),
    /**
     * The map's own digest, served as the ETag.
     *
     * A published map version is immutable, so this never changes for a given
     * version and a revisit costs a 304. It is the *map's* digest, not the
     * configuration's: the configuration changes when an organizer reprices or
     * withdraws a chair, and none of that moves the furniture.
     */
    topologyDigest: topologyDigestSchema,
    geometrySchemaVersion: geometrySchemaVersionSchema,
    canvas: canvasSchema,
    sections: z.array(customerTopologySectionSchema),
    /**
     * Flat, not nested under rows and tables. A renderer draws one pass over
     * every chair, and the availability overlay joins on `venueMapSeatId`
     * without walking a tree.
     */
    seats: z.array(customerTopologySeatSchema),
    /**
     * Explicit companion relationships, by exact occurrence.
     *
     * Here because a client must not derive them. Adjacency does not imply
     * companionship — an aisle, a barrier or a step can sit between two chairs
     * and the canvas does not know — and `isAccessible` says nothing about which
     * chair belongs with which. Only the organizer who walked the room knows, and
     * this is that answer.
     *
     * Both ids are occurrences from the ACTIVE configuration's pinned map
     * version, so they join directly against `seats` above and against
     * `seat-options`.
     *
     * A companion link is physical topology and nothing more. It does not mean
     * the companion chair is free, offered, included in a price, or reserved
     * alongside anything. Availability still comes from `seat-options`, and a
     * customer who wants both chairs asks for both — a hold is never widened on
     * somebody's behalf.
     */
    companions: z.array(z.object({
        accessibleSeatId: z.string().uuid(),
        companionSeatId: z.string().uuid(),
    })),
});
// --- Choosing a chair -------------------------------------------------------
/**
 * What a customer needs to know about the chairs actually on screen.
 *
 * Bounded exactly as the Organizer detail query is, and for the same reason:
 * a working zoom names its few hundred visible occurrences rather than the
 * room. Unlike that query this one carries commercial terms too, because a
 * person choosing a seat needs to know what it costs, and making them fetch
 * that separately would mean two round trips to render one chair.
 */
export const seatOptionsRequestSchema = z.object({
    /** The version the client is looking at. Refused if it is no longer selling. */
    seatingConfigurationVersionId: z.string().uuid(),
    venueMapSeatIds: z
        .array(z.string().uuid())
        .min(1)
        .max(SEAT_DETAIL_MAX * 4),
});
/**
 * One chair, priced and either free or not.
 *
 * No allocation and no frozen product version. Those are how the server knows
 * what to charge and what to draw down; a client that carried them could send
 * them back, and then the price of a seat would be partly the browser's
 * opinion. The product is named because a customer may legitimately be choosing
 * between two of them in the same room.
 */
export const seatOptionSchema = z.object({
    venueMapSeatId: z.string().uuid(),
    saleDisposition: saleDispositionSchema,
    availability: seatAvailabilitySchema,
    /** Present when the chair is offered; absent when it is not for sale. */
    ticketProductId: z.string().uuid().nullable(),
    productName: z.string().nullable(),
    price: moneySchema.nullable(),
    /**
     * Set when this chair belongs to a table sold as one unit.
     *
     * A customer cannot buy it on its own, and a surface that offered it would be
     * setting them up for a refusal. Naming the table lets the UI offer the thing
     * that can actually be bought.
     */
    soldWithTableId: z.string().uuid().nullable(),
});
/**
 * A table a customer can take as one unit, priced.
 *
 * Returned beside the chairs rather than from a route of its own. A chair at a
 * whole-table table is never individually sellable — the database refuses that
 * combination — so a surface holding only `seatOption` rows would know a chair
 * cannot be bought and have nothing to offer in its place. This is that
 * something.
 *
 * `availability` is computed over the table's entire chair set, not the chairs
 * the client happened to ask about. A table is takeable only while all of it is,
 * and a renderer under level-of-detail may be drawing two of its eight chairs.
 */
export const tableOptionSchema = z.object({
    venueMapTableId: z.string().uuid(),
    tableName: z.string(),
    saleMode: tableSaleModeSchema,
    seatCount: z.number().int().min(0),
    availability: seatAvailabilitySchema,
    /** Present when the table is sold whole; absent when its chairs carry the terms. */
    ticketProductId: z.string().uuid().nullable(),
    productName: z.string().nullable(),
    /** Per seat. A client showing a table total multiplies by `seatCount`. */
    unitPrice: moneySchema.nullable(),
    totalPrice: moneySchema.nullable(),
});
export const seatOptionsResponseSchema = z.object({
    requested: z.number().int().min(0),
    seats: z.array(seatOptionSchema),
    /**
     * The tables the requested chairs sit at. Empty for a room without tables,
     * and never a list of every table in the venue — a client is told about the
     * tables it is already drawing.
     */
    tables: z.array(tableOptionSchema),
});
// --- Taking a hold ----------------------------------------------------------
/**
 * What the customer is asking for, named as a commercial act.
 *
 * A table is requested *as a table*. The server expands it to its constituent
 * chairs and claims them atomically, so the browser never has to know which
 * chairs a table has — and therefore never becomes responsible for table-mode
 * semantics it could get wrong.
 */
export const seatSelectionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('SEATS'),
        venueMapSeatIds: z.array(z.string().uuid()).min(1).max(50),
    }),
    z.object({
        kind: z.literal('WHOLE_TABLE'),
        venueMapTableId: z.string().uuid(),
    }),
]);
export const seatHoldRequestSchema = z.object({
    seatingConfigurationVersionId: z.string().uuid(),
    selection: seatSelectionSchema,
});
/**
 * The chairs actually granted, and until when.
 *
 * `seats` is what the server gave, not what was asked for. For a whole table
 * that is every chair at it; for a seat selection it is the same set, because
 * a partial hold is never granted. Either way a client renders this rather than
 * its own request, so what it shows is what it has.
 */
export const seatHoldSchema = z.object({
    seatHoldId: z.string().uuid(),
    seatingConfigurationVersionId: z.string().uuid(),
    /**
     * Authoritative, from the application clock. A client counts down to it and
     * may say "expired" when it passes, but checkout still asks the server —
     * whether this hold can still become a purchase is not a browser's call.
     */
    expiresAt: z.string(),
    seats: z.array(z.object({
        venueMapSeatId: z.string().uuid(),
        /** For display: "Row 12, seat 4". */
        seatLabel: z.string(),
    })),
    /** Set when the hold was taken as a table rather than as loose chairs. */
    venueMapTableId: z.string().uuid().nullable(),
    ticketProductId: z.string().uuid(),
    productName: z.string(),
    unitPrice: moneySchema,
    totalPrice: moneySchema,
});
// --- Where the ticket sits, and where it was sold ---------------------------
/**
 * A chair, as a customer is told about it.
 *
 * Used for two different facts — where the ticket sits *now* and where it was
 * originally sold — so this type says neither. The field carrying it does.
 *
 * Stable against the room changing around it: a later map version renumbering
 * the venue, a reprice, a superseded configuration. None of those move
 * anybody. Only an authorised re-seat does, and that shows up as a different
 * current seat rather than a rewritten one.
 *
 * Extends the existing ticket and order reads rather than living behind an
 * assignment API of its own — a customer asks about their ticket, not about an
 * assignment.
 */
export const ticketSeatSchema = z.object({
    eventSessionId: z.string().uuid(),
    venueMapSeatId: z.string().uuid(),
    sectionName: z.string(),
    rowLabel: z.string().nullable(),
    seatLabel: z.string(),
    /** Set when the chair was bought as part of a table. */
    tableName: z.string().nullable(),
});
/**
 * Why a seat moved, in words a customer can be shown.
 *
 * Deliberately one coarse category rather than the organizer's own reason. The
 * internal code can carry operational or safety meaning — an obstructed
 * sightline, a broken seat, a security decision about a particular row — and
 * none of that is a customer's to read from their ticket. Mapping is explicit
 * in the server, so adding a category is a decision somebody makes rather than
 * a detail that leaks.
 */
export const seatChangeReasonSchema = z.enum(['ORGANIZER_REASSIGNMENT']);
/**
 * That the seat changed, and roughly what happened.
 *
 * Present only when a ticket has actually been moved. Null is the ordinary
 * case and means the customer still sits where they bought — which a surface
 * needs to be able to tell apart from "moved, but we lost the history".
 *
 * A summary rather than a log: how many times and when last, not a list of
 * every chair the ticket has occupied. A customer needs to understand that
 * their seat changed and trust that their purchase is intact; the full
 * movement history is an organizer and support concern.
 */
export const seatChangeSchema = z.object({
    lastMovedAt: z.string(),
    moveCount: z.number().int().min(1),
    reason: seatChangeReasonSchema,
});
// --- Refusals ---------------------------------------------------------------
/**
 * Why a customer's seat request was refused.
 *
 * Separate from the organizer vocabulary, and deliberately not a copy of it
 * with friendlier wording. The conditions sometimes rhyme; the remedies do not.
 * An organizer told a table's mode conflicts goes and fixes the configuration.
 * A customer told the same thing can only be offered the thing they *can* buy.
 *
 * One reason is reused rather than restated: a selection spanning two
 * allocations means the same thing and asks for the same action on both sides —
 * choose seats drawn from the same inventory, or take them separately — so it
 * keeps `SEATING_ACQUISITION_SPANS_ALLOCATIONS` from the 10B vocabulary.
 *
 * A hold belonging to somebody else is absent from this list on purpose. It is
 * refused as not-found, because telling a stranger that a hold exists but is
 * not theirs is an oracle (ADR 0009).
 */
export const SEAT_PURCHASE_REFUSAL_REASONS = [
    /**
     * The room changed while the customer was choosing — the organizer activated
     * a new configuration. Remedy: reload the room and choose again. Their
     * selection may still be free, but it must be re-made against what is now
     * selling.
     */
    'SEAT_SELECTION_CONFIGURATION_STALE',
    /**
     * One or more chosen chairs are no longer free. Somebody else was quicker, or
     * the organizer took them off sale. Details name the chairs that are gone, so
     * a surface can grey exactly those rather than clearing the whole selection.
     */
    'SEAT_SELECTION_UNAVAILABLE',
    /**
     * The hold lapsed before checkout reached it. The chairs went back on sale
     * when it did. Remedy: choose again — and this is the answer whatever a
     * client's own countdown said, because the server decides.
     */
    'SEAT_HOLD_EXPIRED',
    /**
     * A single chair was requested at a table sold as one unit. Remedy: buy the
     * table. Details name it.
     */
    'SEAT_SELECTION_TABLE_SOLD_WHOLE',
    /**
     * A whole table was requested where the chairs are sold individually.
     * Remedy: choose the chairs wanted.
     */
    'SEAT_SELECTION_TABLE_SOLD_BY_SEAT',
    /**
     * Checkout has already begun for these chairs — a second tab, or a retry
     * without an idempotency key. Not an error the buyer caused, and not a reason
     * to choose again: their seats are safe and the order exists. Remedy: go to
     * the order that already has them.
     *
     * Refused by `checkout_quote_lines_one_per_hold` rather than by a check, so
     * two tabs racing get one order between them however they interleave.
     */
    'SEAT_HOLD_ALREADY_IN_CHECKOUT',
];
export const seatPurchaseRefusalReasonSchema = z.enum(SEAT_PURCHASE_REFUSAL_REASONS);
/**
 * Narrows a refusal this client understands, or `null`.
 *
 * Null rather than throwing, so a surface a release behind a new reason shows
 * the generic refusal instead of matching nothing and rendering an empty state.
 */
export function asSeatPurchaseRefusalReason(value) {
    const parsed = seatPurchaseRefusalReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// --- Moving a sold seat (organizer) -----------------------------------------
/**
 * What an organizer may say when moving a ticket.
 *
 * Operational intent and nothing else. There is no product, no version, no
 * allocation, no price and no commitment here, and their absence is the
 * contract: a move is not a sale, and a caller that could name commercial terms
 * could change what somebody paid for by moving their chair.
 *
 * `expectedCurrentSeatId` is optimistic concurrency, not decoration. Two
 * organizers looking at the same list will both believe the ticket is where it
 * was when they loaded it, and the second one must be told rather than
 * silently overwriting the first.
 */
export const reseatRequestSchema = z.object({
    /** Where the caller believes the ticket is. Refused if it has moved since. */
    expectedCurrentSeatId: z.string().uuid(),
    destinationSeatId: z.string().uuid(),
    /**
     * The organizer's own reason, recorded in the audit trail and never shown to
     * the customer — it can carry operational or safety meaning. Customers are
     * told only that an organizer moved them.
     */
    reasonCode: z
        .string()
        .trim()
        .min(2)
        .max(64)
        .regex(/^[A-Z][A-Z0-9_]*$/, 'A reason code is upper snake case'),
    reasonDetail: z.string().trim().max(500).nullish(),
    /**
     * Retry convergence. A move replayed with the same key returns the original
     * result rather than moving anybody twice or refusing as stale.
     */
    idempotencyKey: z.string().trim().min(8).max(200),
});
export const reseatResultSchema = z.object({
    reassignmentId: z.string().uuid(),
    ticketId: z.string().uuid(),
    currentSeatId: z.string().uuid(),
});
//# sourceMappingURL=seat-purchase-schemas.js.map